import { faker } from '@faker-js/faker';

/** Delivery status — drives the square/mono/bold colored status chip. */
export type DeliveryStatus = 'delivered' | 'failed' | 'pending' | 'bounced';

export const DELIVERY_STATUSES: readonly DeliveryStatus[] = [
	'delivered',
	'failed',
	'pending',
	'bounced',
];

/** A single point on a record's delivery lifecycle — drives the event timeline. */
export type DeliveryEventType =
	| 'sent'
	| 'delivered'
	| 'bounced'
	| 'failed'
	| 'pending';

/** One event in a record's chronological delivery history. */
export interface DeliveryEvent {
	type: DeliveryEventType;
	/** ISO-8601 timestamp of this event. */
	timestamp: string;
}

/** Severity/sentiment tier for an insight finding — drives the finding chip color. */
export type DeliveryInsightTier =
	| 'needs-attention'
	| 'possible-improvement'
	| 'doing-great';

/**
 * A delivery-diagnostic finding for a record: bounce/failure reason, provider
 * delivery note, or retry history. Domain-appropriate delivery facts ONLY —
 * never email-content-quality checks (spam score, subject length, etc.).
 */
export interface DeliveryInsight {
	tier: DeliveryInsightTier;
	message: string;
}

/** One row in the delivery-log table. */
export interface DeliveryRecord {
	id: string;
	recipient: string;
	subject: string;
	provider: string;
	status: DeliveryStatus;
	/** ISO-8601 timestamp of the delivery attempt. */
	timestamp: string;
	/** Chronological delivery lifecycle, causally consistent with `status`. */
	events: DeliveryEvent[];
	/** Plain-text rendering of the email body. */
	contentPlainText: string;
	/** Small plausible HTML rendering of the email body. */
	contentHtml: string;
	/** Raw SMTP/MIME-ish blob (plausible, not literal wire format). */
	contentRaw: string;
	/** Delivery-diagnostic findings, each tagged with a tier. */
	insights: DeliveryInsight[];
}

/** Full delivery-log payload — the page filters/searches/paginates client-side. */
export interface DeliveryLogData {
	records: DeliveryRecord[];
	total: number;
}

const PROVIDERS = ['SES', 'SendGrid', 'Postmark', 'Mailgun'] as const;

const DEFAULT_COUNT = 120;

/**
 * Build a status-consistent event history for one record. `sent` is always
 * first; the terminal event mirrors the record's status. `failed` may carry
 * retry attempts between sent and the terminal failure. `pending` stops at
 * `sent` (no terminal event yet). All events precede `sentAt` monotonically.
 */
function buildEvents(status: DeliveryStatus, sentAt: Date): DeliveryEvent[] {
	const events: DeliveryEvent[] = [
		{ type: 'sent', timestamp: sentAt.toISOString() },
	];

	// Terminal events land shortly after send; keep them ordered forward.
	let cursor = sentAt.getTime();
	const advance = (): string => {
		cursor += faker.number.int({ min: 2_000, max: 90_000 });
		return new Date(cursor).toISOString();
	};

	switch (status) {
		case 'delivered':
			events.push({ type: 'delivered', timestamp: advance() });
			break;
		case 'bounced':
			events.push({ type: 'bounced', timestamp: advance() });
			break;
		case 'failed': {
			// 0-2 retry attempts (rendered as interstitial `pending` beats) before
			// the terminal failure.
			const retries = faker.number.int({ min: 0, max: 2 });
			for (let i = 0; i < retries; i++) {
				events.push({ type: 'pending', timestamp: advance() });
			}
			events.push({ type: 'failed', timestamp: advance() });
			break;
		}
		case 'pending':
			// Still in flight — no terminal event.
			break;
	}

	return events;
}

const BOUNCE_REASONS = [
	'Recipient mailbox does not exist (550 5.1.1)',
	'Recipient mailbox full — over quota (552 5.2.2)',
	'Remote host rejected the recipient domain (554 5.4.4)',
	'Message refused as spam by the receiving server (550 5.7.1)',
];

const FAILURE_REASONS = [
	'Connection to the receiving MTA timed out after 3 attempts',
	'TLS handshake failed against the recipient server',
	'Message expired in the retry queue before delivery',
	'Provider throttled the sending domain (429)',
];

const PROVIDER_NOTES = [
	'Delivered to the recipient inbox; no engagement events reported yet.',
	'Accepted by the provider and queued for the destination MX.',
	'Recipient opened the message within 4 minutes of delivery.',
	'Routed through a shared IP pool with a healthy reputation score.',
];

/** Build status-appropriate delivery-diagnostic findings for one record. */
function buildInsights(
	status: DeliveryStatus,
	events: DeliveryEvent[],
): DeliveryInsight[] {
	const insights: DeliveryInsight[] = [];
	const retryAttempts = events.filter((e) => e.type === 'pending').length;

	switch (status) {
		case 'delivered':
			insights.push({
				tier: 'doing-great',
				message: faker.helpers.arrayElement(PROVIDER_NOTES),
			});
			break;
		case 'pending':
			insights.push({
				tier: 'possible-improvement',
				message:
					'Message is still in flight — awaiting a delivery or bounce receipt from the provider.',
			});
			break;
		case 'bounced':
			insights.push({
				tier: 'needs-attention',
				message: faker.helpers.arrayElement(BOUNCE_REASONS),
			});
			insights.push({
				tier: 'possible-improvement',
				message:
					'Consider suppressing this recipient to protect the sending-domain reputation.',
			});
			break;
		case 'failed':
			insights.push({
				tier: 'needs-attention',
				message: faker.helpers.arrayElement(FAILURE_REASONS),
			});
			if (retryAttempts > 0) {
				insights.push({
					tier: 'possible-improvement',
					message: `Delivery was retried ${retryAttempts} time${
						retryAttempts === 1 ? '' : 's'
					} before failing.`,
				});
			}
			break;
	}

	return insights;
}

/** Build a mock email body across plain-text, HTML, and raw MIME renderings. */
function buildContent(
	subject: string,
	recipient: string,
	provider: string,
	messageId: string,
	sentAt: Date,
): Pick<DeliveryRecord, 'contentPlainText' | 'contentHtml' | 'contentRaw'> {
	const greeting = `Hi ${faker.person.firstName()},`;
	const body = faker.lorem.paragraph();
	const closing = `Thanks,\nThe ${faker.company.name()} Team`;

	const contentPlainText = `${greeting}\n\n${body}\n\n${closing}`;

	const contentHtml =
		`<p>${greeting}</p>\n` +
		`<p>${body}</p>\n` +
		`<p>Thanks,<br />The ${faker.company.name()} Team</p>`;

	const contentRaw = [
		`Message-ID: <${messageId}@${provider.toLowerCase()}.mail>`,
		`Date: ${sentAt.toUTCString()}`,
		`From: no-reply@${faker.internet.domainName()}`,
		`To: ${recipient}`,
		`Subject: ${subject}`,
		'MIME-Version: 1.0',
		'Content-Type: text/plain; charset=UTF-8',
		'',
		contentPlainText,
	].join('\n');

	return { contentPlainText, contentHtml, contentRaw };
}

export function getDeliveryLogMock(count = DEFAULT_COUNT): DeliveryLogData {
	const records: DeliveryRecord[] = Array.from({ length: count }, () => {
		const id = faker.string.uuid();
		const recipient = faker.internet.email().toLowerCase();
		const subject = faker.helpers.arrayElement([
			faker.company.catchPhrase(),
			`Your ${faker.commerce.productName()} order`,
			`Policy renewal notice — ${faker.date.month()}`,
			`Statement ready for ${faker.finance.accountName()}`,
		]);
		const provider = faker.helpers.arrayElement(PROVIDERS);
		const status = faker.helpers.weightedArrayElement<DeliveryStatus>([
			{ weight: 80, value: 'delivered' },
			{ weight: 8, value: 'failed' },
			{ weight: 7, value: 'pending' },
			{ weight: 5, value: 'bounced' },
		]);
		const sentAt = faker.date.recent({ days: 14 });
		const timestamp = sentAt.toISOString();
		const events = buildEvents(status, sentAt);

		return {
			id,
			recipient,
			subject,
			provider,
			status,
			timestamp,
			events,
			...buildContent(subject, recipient, provider, id, sentAt),
			insights: buildInsights(status, events),
		};
	}).sort((a, b) => b.timestamp.localeCompare(a.timestamp));

	return { records, total: records.length };
}
